import {
  getNestedSubelementsByType,
  getMostRecentFieldValue,
  getSlsTypeByValue,
  getPersonName,
  getNestedOrFloatingElementById,
  getOtherPersonId,
  getRelationshipLabel,
  getNextUuid,
  getMostRecentFieldResource,
  getMostRecentFieldValueInArray,
  isPlaceField,
  isDateField,
  getDisplayLabelForFieldType,
} from '@fs/zion-sls'

/**
 * Retrieves related person data based on relationship information.
 *
 * @param {Object} relationshipData - The relationship data object.
 * @param {Object} person - The current person object.
 * @param {Array} images - Array of images from SLS state context.
 * @returns {Object} An object with the related person's ID as `key` and their name as `primaryText`.
 */
const getRelatedPersonData = (relationshipData, person, images) => {
  const otherPersonId = getOtherPersonId(relationshipData, person)
  const otherPerson = getNestedOrFloatingElementById(images, otherPersonId)

  return {
    key: otherPersonId,
    primaryText: getPersonName({ person: otherPerson }) || 'unknown',
    recordId: otherPerson?.recordId || null, // For quick checking of cross-record relationships
  }
}

/**
 * Checks if the given eventSlsType contains the 'RELATIONSHIP' element type.
 *
 * @param {Object} eventSlsType - The event type object to check.
 * @param {string[]} [eventSlsType.elementTypes] - Array of element type strings.
 * @returns {boolean} True if 'RELATIONSHIP' is included in elementTypes, otherwise false.
 */
export const containsRelationshipType = (eventSlsType) => {
  return eventSlsType?.elementTypes?.includes('RELATIONSHIP')
}

/**
 * Retrieves the original primary text value from defaultValues for a specific field path.
 * Helper function to reduce duplication when accessing deeply nested form default values.
 *
 * @param {Object} defaultValues - The form's default values object.
 * @param {boolean} isPrimary - Whether this is a primary event (true) or additional event (false).
 * @param {number} eventIndex - Index of the event in the events array.
 * @param {string} fieldPath - The field path (e.g., 'dates', 'places', 'other').
 * @param {number} itemIndex - Index of the item in the field array.
 * @param {number} [subElIndex] - Optional index of the subElement (for nested subElements).
 * @returns {string|undefined} The original primaryText value or undefined if not found.
 */
export const getOriginalFormValue = (defaultValues, isPrimary, eventIndex, fieldPath, itemIndex, subElIndex) => {
  const eventType = isPrimary ? 'primary' : 'additional'
  const basePath = defaultValues?.events?.[eventType]?.[eventIndex]?.[fieldPath]?.[itemIndex]

  if (subElIndex !== undefined) {
    return basePath?.subElements?.[subElIndex]?.formValue?.primaryText
  }
  return basePath?.formValue?.primaryText
}

/**
 * Validates if a linked event field has changed and returns a warning message if needed.
 * Used for form field validation to warn users when editing linked events.
 *
 * @param {boolean} isLinkedEvent - Whether this is a linked event affecting multiple people.
 * @param {*} currentValue - The current value being validated.
 * @param {*} originalValue - The original value from defaultValues.
 * @param {function} t - Translation function (from useTranslation hook).
 * @returns {string|null} Warning message if value changed on a linked event, otherwise null.
 */
export const validateLinkedEventFieldChange = (isLinkedEvent, currentValue, originalValue, t) => {
  if (isLinkedEvent && currentValue !== originalValue) {
    return t('edit-events.linked-field-change-warning', 'Changes will be made on all who are linked.')
  }
  return null
}

/**
 * Maps an array of elements and attaches a `formValue` to each subElement based
 * on its first fieldValue using `getAutosuggestValue`.
 * Preserves typeData from existing subElements or sets default { editable: true } for new ones.
 *
 * @param {Array<Object>} elements - Array of element objects, each may contain a `subElements` array.
 * @returns {Array<Object>} New array with subElements mapped and `formValue` added to each subElement when applicable.
 */
export function mapWithFormValue(elements) {
  return elements.map((element) => ({
    ...element,
    subElements: Array.isArray(element.subElements)
      ? element.subElements.map((subEl) => ({
          ...subEl,
          formValue: getAutosuggestValue(subEl),
          typeData: subEl.typeData || { editable: true },
        }))
      : element.subElements,
  }))
}

/**
 * Generates an empty event part object for a given field type.
 *
 * @param {'DATE'|'PLACE'} fieldType - The type of the field to be used in the event part.
 * @param {Object} [slsTypes] - Optional SLS types object for getting displayLabel
 * @returns {{ subElements: [{ id: null, fieldType: string, displayLabel: string, formValue: { originalText: string, standard: null }, typeData: { editable: boolean } }] }}
 *   An object containing a single subElement with default values.
 */
const generateEmptySubElements = (fieldType, slsTypes) => ({
  subElements: [
    {
      id: getNextUuid(),
      fieldType,
      displayLabel: getDisplayLabelForFieldType(fieldType, slsTypes) || fieldType,
      formValue: { originalText: '', standard: null },
      typeData: { editable: true },
    },
  ],
})

/**
 * Returns the default scalar values for an event (without dates, places, other arrays).
 * Useful for creating a clean base when you already have populated arrays.
 *
 * @param {boolean} isPrimary - Whether this is a primary event.
 * @returns {Object} An object with default scalar values (nulls and empty arrays for metadata).
 */
export function getEventDefaults(isPrimary) {
  return {
    id: null,
    eventType: null,
    primary: isPrimary,
    otherPerson: null,
    relationshipId: null,
    superElements: [],
    existingEventId: null,
    confirmedUnlinks: [],
    confirmedAddLinks: [],
  }
}

/**
 * Creates an empty event placeholder with default structure.
 *
 * @param {boolean} isPrimary - Whether this is a primary event.
 * @param {Object} [slsTypes] - Optional SLS types object for getting displayLabels
 * @returns {Object} An empty event object with null/empty default values.
 */
export function createEmptyEvent(isPrimary, slsTypes) {
  return {
    ...getEventDefaults(isPrimary),
    places: [generateEmptySubElements('PLACE', slsTypes)],
    dates: [generateEmptySubElements('DATE', slsTypes)],
    other: [],
  }
}

/**
 * Creates an event object from raw event data, extracting and mapping places, dates, and other fields.
 *
 * @param {Object} event - The raw event object with subElements.
 * @param {Object} slsTypes - Optional SLS types object for event type lookup.
 * @param {boolean} isPrimary - Whether this is a primary event.
 * @param {Object} person - The current person object for relationship context.
 * @param {Array} images - Array of images from SLS state context.
 * @returns {Object} A structured event object ready for form consumption.
 */
function createEventObject(event, slsTypes, isPrimary, person, images) {
  // Extract places and dates from direct children
  const places = []
  const dates = []

  event.subElements?.forEach((subElement) => {
    if (subElement.elementType === 'PLACE') {
      places.push(subElement)
    } else if (subElement.elementType === 'DATE') {
      dates.push(subElement)
    }
  })

  // Extract only FIELD elements that are DIRECT children of the event
  // Do NOT extract fields nested inside PLACE or DATE elements
  // This prevents duplicate field rendering (fields nested in PLACE/DATE will be rendered by SubElements)
  const allFields = event.subElements?.filter((el) => el.elementType === 'FIELD') || []
  const otherFields = allFields.filter((field) => field.fieldType !== 'EVENT_TYPE')

  // Map other fields directly since they are FIELD elements, not wrapped structures
  const other = otherFields.map((field) => ({
    ...field,
    formValue: getAutosuggestValue(field),
    typeData: field.typeData || { editable: true },
  }))

  // Ensure at least one PLACE and one DATE element
  const ensuredPlaces = places.length > 0 ? places : [generateEmptySubElements('PLACE', slsTypes)]
  const ensuredDates = dates.length > 0 ? dates : [generateEmptySubElements('DATE', slsTypes)]

  const slsEventType = getSlsTypeByValue(event?.eventType, slsTypes)
  const isRelationshipEvent = containsRelationshipType(slsEventType) || false
  const { relationshipData } = event

  // If this is a relationship event, get the related person data
  let otherPerson = null
  if (isRelationshipEvent && relationshipData) {
    otherPerson = getRelatedPersonData(relationshipData, person, images)
  }

  return {
    id: event.id || null,
    eventType: event?.eventType
      ? {
          key: event?.eventType,
          primaryText: getEventTypeFieldValue(event, slsTypes),
          isRelationshipEvent,
          relType: slsEventType?.relType || null,
        }
      : null,
    places: ensureDefaultStandardField(mapWithFormValue(ensuredPlaces), 'PLACE', slsTypes),
    dates: ensureDefaultStandardField(mapWithFormValue(ensuredDates), 'DATE', slsTypes),
    other,
    primary: isPrimary,
    // Include superElements to track linked persons
    superElements: event.superElements || [],
    // Store the existing event ID if this is an existing event
    existingEventId: event.id || null,
    // Track confirmed unlinks for this event
    confirmedUnlinks: [],
    confirmedAddLinks: [],
    otherPerson,
    relationshipId: relationshipData?.id || null,
    relationshipData: relationshipData || null,
  }
}

/**
 * Retrieves all EVENT subelements directly under a person, including those related to relationships.
 * For events originating from relationships, attaches relationship data to the event.
 *
 * @param {Object} person - The person object.
 * @returns {Array<Object>} Array of event objects, each with optional relationship data.
 */
export const getEventsFromPerson = (person) => {
  if (!person) return []

  // This will get all EVENT subelements directly under the person even if they are related to relationships
  const personEvents = getNestedSubelementsByType(person, 'EVENT') || []

  // Map event IDs to their corresponding relationship data for quick lookup
  const personRelationships = getNestedSubelementsByType(person, 'RELATIONSHIP') || []
  const eventsMap = new Map()
  personRelationships.forEach((relationship) => {
    const event = relationship?.subElements?.find((subEl) => subEl.elementType === 'EVENT')
    if (event?.id) {
      // Store the relationship data for this event ID
      eventsMap.set(event.id, relationship)
    }
  })

  // Attach relationship data to events that originate from relationships
  const processedPersonEvents = personEvents.map((event) => ({
    ...event,
    fromRelationship: eventsMap.has(event.id),
    relationshipData: eventsMap.get(event.id),
  }))

  return processedPersonEvents
}

/**
 * Sort function that moves an object (sls event) with primary=true to the top of a list.
 * @param {Object} a - Current object being compared.
 * @param {Object} b - Next object being compared.
 * @returns {number}
 */
export function sortPrimaryToTop(a, b) {
  const aPrimary = a?.primary ? 1 : 0
  const bPrimary = b?.primary ? 1 : 0
  if (bPrimary !== aPrimary) return bPrimary - aPrimary
  return 0
}

/**
 * Retrieves the primary event from a person's record.
 * The primary event is an event with a primaryOnly type found on the record element.
 *
 * @param {Object} person - The person object.
 * @param {Array} images - Array of images from SLS state context.
 * @returns {Object|null} The record's primary event or null if not found.
 */
export function getRecordPrimaryEvent(person, images) {
  if (!person?.recordId) return null

  const record = getNestedOrFloatingElementById(images, person.recordId)
  if (!record) return null

  const recordEvents = getNestedSubelementsByType(record, 'EVENT') || []
  return recordEvents.find((ev) => ev.primary)
}

/**
 * Ensures a primary event exists for the person, creating an empty one if necessary.
 * Primary events come from the person's record, not from the person being primary.
 * For new records with no primary event, creates required fields for validation.
 *
 * @param {Object} person - The person object.
 * @param {Array} images - Array of images from SLS state context.
 * @param {Object} slsTypes - SLS types object for event type lookup.
 * @param {Array<Object>} existingPrimaryEvents - Array of existing primary event objects.
 * @returns {Array<Object>} Array with at least one primary event object.
 */
function ensurePrimaryEventExists(person, images, slsTypes, existingPrimaryEvents) {
  if (existingPrimaryEvents.length > 0) {
    return existingPrimaryEvents
  }

  // Try to get the record's primary event
  const recordPrimaryEvent = getRecordPrimaryEvent(person, images)

  if (recordPrimaryEvent) {
    // Found the record's primary event - use it for all persons in the record
    const eventObject = createEventObject(recordPrimaryEvent, slsTypes, true, person, images)
    return [eventObject]
  }

  // No primary event found - create empty event with required fields for validation
  // This applies to both new records and persons without a record
  return [createEmptyEvent(true, slsTypes)]
}

/**
 * Separates events into primary and additional arrays.
 * Primary events are those marked with primary=true on the event.
 *
 * @param {Array<Object>} events - Array of event objects.
 * @param {Object} slsTypes - SLS types object for event type lookup.
 * @param {Object} person - The person object.
 * @param {Array} images - Array of images from SLS state context.
 * @returns {{ primary: Array<Object>, additional: Array<Object> }} Object with separated events.
 */
function separateEventsIntoPrimaryAndAdditional(events, slsTypes, person, images) {
  const primaryEvents = []
  const additionalEvents = []

  events?.forEach((event) => {
    const eventObject = createEventObject(event, slsTypes, event.primary, person, images)
    if (event.primary) {
      primaryEvents.push(eventObject)
    } else {
      additionalEvents.push(eventObject)
    }
  })

  return { primary: primaryEvents, additional: additionalEvents }
}

/**
 * Creates a map of event IDs to their original event data for tracking deletions.
 * Events without an ID are not included in the map.
 *
 * @param {Array<Object>} events - Array of event objects.
 * @returns {Object} Map of event IDs to event data.
 */
function createEventsIdMap(events) {
  const eventsById = {}
  events?.forEach((event) => {
    if (event?.id) {
      eventsById[event.id] = { ...event }
    }
  })
  return eventsById
}

/**
 * Prepares default form values for events for use in forms.
 * Splits provided events into primary and additional arrays, mapping places, dates, and other fields through `mapWithFormValue`.
 * If no primary event is present, inserts an empty placeholder for validation.
 * Additional events are used as-is without placeholders.
 * Also returns a map of event IDs to their original event data for tracking deletions.
 *
 * PRIMARY EVENT LOGIC:
 * - Primary events come from the person's record (the record element), not from the person being primary
 * - All persons in a record share the same primary event (the record's primaryOnly event)
 * - For new records with no primary event, we create required fields for validation that will be created on save
 * - Primary event is required for ALL persons regardless of whether person.primary is true
 *
 * @param {Array<Object>} events - Array of event objects, each with `subElements` and metadata.
 * @param {Object} [slsTypes] - Optional SLS types object for event type lookup.
 * @param {Object} person - The current selected person object.
 * @param {Object} images - The images object from SLS state for context.
 * @returns {{ events: { primary: Array<Object>, additional: Array<Object> }, eventsById: Object, primaryEventId: String }}
 *   An object containing grouped events for form defaults and a map of event IDs to event data.
 *   NOTE: Events without an ID will not be included in the eventsById map.
 */
export function prepareEventsDefaultValues(events, slsTypes, person, images) {
  // Separate events into primary and additional
  const { primary: primaryEvents, additional: additionalEvents } = separateEventsIntoPrimaryAndAdditional(
    events,
    slsTypes,
    person,
    images
  )

  // Ensure primary event exists (from record or create empty for validation)
  const finalPrimaryEvents = ensurePrimaryEventExists(person, images, slsTypes, primaryEvents)

  // Ensure at least one additional event placeholder exists
  const finalAdditionalEvents = additionalEvents.length > 0 ? additionalEvents : [createEmptyEvent(false, slsTypes)]

  // Create event ID map for tracking deletions
  const eventsById = createEventsIdMap(events)

  return {
    events: { primary: finalPrimaryEvents, additional: finalAdditionalEvents },
    eventsById,
    primaryEventId: finalPrimaryEvents[0].id,
  }
}

function getFieldIdsFromEventObject(event) {
  const dateFieldIds = event.dates.flatMap((date) => date.subElements?.map((se) => se.id)) || []
  const placeFieldIds = event.places.flatMap((date) => date.subElements?.map((se) => se.id)) || []
  const otherFieldIds = event.other.flatMap((date) => date.subElements?.map((se) => se.id)) || []
  return [...dateFieldIds, ...placeFieldIds, ...otherFieldIds].filter(Boolean)
}

export function getFieldIdsFromFormValues(formValues) {
  const primaryFieldIds = formValues?.events?.primary?.flatMap(getFieldIdsFromEventObject) || []
  const additionalFieldIds = formValues?.events?.additional?.flatMap(getFieldIdsFromEventObject) || []
  return [...primaryFieldIds, ...additionalFieldIds]
}

/**
 * If a normalizedValue is associated with a resource, convert it to a form value usable with the autosuggest in standardize mode.
 * @param {Object} field - field from SLS, containing a fieldType
 * @param {String} resource - the resource URI associated with the normalized value
 * @param {String} textValue = the normalized or origText value.
 * @returns {{originalText: string|null, standard: {id: *|null, standardizedText, formalText: *|null}|null}}
 */
function createFormFieldValueFromResource(field, resource, textValue) {
  return {
    originalText: resource ? null : textValue || '',
    standard: resource
      ? {
          id: isPlaceField(field) ? resource.split('/').at(-1) : null,
          standardizedText: textValue || '',
          formalText: isDateField(field) ? resource : null,
        }
      : null,
  }
}

/**
 * Converts a raw fieldValue into an autosuggest-friendly value used by form controls.
 *
 * @param {Object} field - The field object containing fieldValues, which may contain `normalizedValues` or `origValue`.
 * @returns {{key?: (string|number), primaryText?: (string|null), originalText?: (string|null), standard: (Object|null)}} Normalized autosuggest value for form fields.
 *   If fieldValue is null/undefined, returns { primaryText: '', standard: null }.
 *   If normalizedValues exists but text is missing, defaults to empty string.
 *   For PLACE/DATE fields, returns an object with { key, originalText, standard }.
 */
export function getAutosuggestValue(field) {
  const fieldValue = getMostRecentFieldValueInArray(field)
  if (!fieldValue) {
    return isPlaceField(field) || isDateField(field)
      ? { originalText: '', standard: null }
      : { primaryText: '', standard: null }
  }
  const textValue = getMostRecentFieldValue(field)
  if (isPlaceField(field) || isDateField(field)) {
    const resource = getMostRecentFieldResource(field)
    return createFormFieldValueFromResource(field, resource, textValue)
  }
  return { key: fieldValue.id, primaryText: textValue || '', standard: null }
}

/**
 * Retrieves the most recent event type field value from an event object.
 *
 * @param {Object} event - The event object containing subElements.
 * @param {Object} [slsTypes] - Optional SLS types object containing events array for fallback lookup.
 * @returns {any} The most recent field value for the event type, or undefined if not found.
 */
export function getEventTypeFieldValue(event, slsTypes) {
  // First try to get EVENT_TYPE from subelements (existing behavior)
  const eventTypeField = getNestedSubelementsByType(event, 'EVENT_TYPE')[0]
  const eventType = getMostRecentFieldValue(eventTypeField)

  // If found, return it
  if (eventType) {
    return eventType
  }

  // Fallback: Look up event.eventType in slsTypes.events to get the displayLabel
  if (slsTypes?.events && event?.eventType) {
    const eventTypeDefinition = slsTypes.events.find((type) => type.eventType === event.eventType)
    if (eventTypeDefinition?.displayLabel) {
      return eventTypeDefinition.displayLabel
    }
  }

  // Return empty string if neither source has the event type
  return ''
}

/**
 * Maps an array of elements/fields to formatted field values and filters out empty values.
 * @private
 * @param {Array} items - Array of elements or fields to map
 * @param {boolean} [allowRawValue=false] - Whether to use raw value instead of most recent field value from the getMostRecentFieldValue function
 * @returns {Array<{id: string, displayLabel: string, value: string}>} Formatted field values with non-empty values
 */
function mapToFieldValues(items, allowRawValue = false) {
  return items
    .map((item) => ({
      id: item.id,
      displayLabel: item.displayLabel,
      value: allowRawValue ? item.value : getMostRecentFieldValue(item),
    }))
    .filter((item) => item.value && item.value.trim() !== '')
}

export const getRelationshipFieldValues = ({ relationship, images, person }) => {
  if (!relationship?.id) return { label: '', name: '' }

  const otherPersonId = getOtherPersonId(relationship, person)
  const otherPerson = getNestedOrFloatingElementById(images, otherPersonId)
  const order = relationship?.superElements?.find((supEl) => supEl.id === otherPersonId)?.order
  const otherPersonName = getPersonName({ person: otherPerson })
  const typeData = relationship?.typeData

  const displayLabel =
    (otherPerson &&
      order &&
      typeData &&
      getRelationshipLabel({
        otherPerson,
        order,
        typeData: relationship?.typeData,
      })) ||
    ''

  return { id: relationship?.id, displayLabel, value: otherPersonName }
}

/**
 * Extracts and formats field values from event elements for display.
 *
 * For primary events, returns aggregated element values (e.g., "Date: 1920").
 * For non-primary events, returns individual field values (e.g., "Day: 15", "Month: May", "Year: 1920").
 *
 * @param {Object} event - The event object containing subElements
 * @param {string} elementType - Type of element to extract ('DATE' or 'PLACE')
 * @param {Object} [images] - Optional images object for relationship context
 * @param {Object} [person] - Optional person object for relationship context
 * @returns {Array<{id: string, displayLabel: string, value: string}>} Array of formatted field values with non-empty values
 */
export function getElementFieldValues(event, elementType, images, person) {
  // Get elements of specified type (DATE or PLACE) - using zion-sls utility for recursive search
  const elements = getNestedSubelementsByType(event, elementType)

  // For relationship events, return relationship related data
  if (event?.relationshipData && images && person) {
    const relationshipFieldValues = getRelationshipFieldValues({ relationship: event.relationshipData, images, person })
    return mapToFieldValues([relationshipFieldValues], true)
  }

  // Return all individual field values
  const fields = elements.flatMap((element) => element.subElements?.filter((el) => el.elementType === 'FIELD') || [])

  return mapToFieldValues(fields)
}

/**
 * Converts a field element to a form value object suitable for autosuggest components.
 *
 * @param {Object} fieldElement - The field element to convert
 * @returns {{primaryText: string}} Form value object
 */
export function getFormValueFromField(fieldElement) {
  if (!fieldElement) {
    return { primaryText: '' }
  }

  const value = getMostRecentFieldValue(fieldElement)
  const autosuggestValue = getAutosuggestValue(fieldElement)
  const standard = autosuggestValue?.standard || null
  const canBeStandardized = isPlaceField(fieldElement) || isDateField(fieldElement)

  // Handle PLACE and DATE fields with standardization logic
  if (canBeStandardized) {
    return autosuggestValue
  }

  // Other fields (non-PLACE/DATE) for supporting non-standardized fields
  return { primaryText: value || '', standard }
}

/**
 * Given a list of elements (DATE or PLACE intermediary structures), it will return existing fields and fill in missing standard fields
 * @param {Array} elements - list of objects with elementType="DATE" or "PLACE"
 * @param {String} fieldType - "DATE" or "PLACE"
 * @param {Object} [slsTypes] - Optional SLS types object for getting displayLabels
 * @returns {*}
 */
export function ensureDefaultStandardField(elements, fieldType, slsTypes) {
  if (fieldType === 'DATE' || fieldType === 'PLACE') {
    const displayLabel = getDisplayLabelForFieldType(fieldType, slsTypes) || fieldType

    return elements.map((el) => {
      let subElements = el.subElements
      if (!subElements) {
        subElements = [
          {
            id: getNextUuid(),
            fieldType,
            displayLabel,
            formValue: { originalText: '', standard: null },
            typeData: { editable: true },
          },
        ]
      } else if (!subElements.some((se) => se.fieldType === fieldType)) {
        subElements = [
          ...el.subElements,
          {
            id: getNextUuid(),
            fieldType,
            displayLabel,
            formValue: { originalText: '', standard: null },
            typeData: { editable: true },
          },
        ]
      }
      return { ...el, subElements }
    })
  }
  return elements
}

/**
 * Maps elements (DATE, PLACE, or FIELD) to form values with their subElements structure.
 * This function handles the transformation of SLS elements into a format suitable for form consumption.
 *
 * @param {Array<Object>} elements - Array of elements to map (DATE, PLACE, or FIELD elements)
 * @param {string} fieldType - The field type to use for elements without nested subElements (e.g., 'DATE', 'PLACE')
 * @param {Object} [slsTypes] - Optional SLS types object for getting displayLabels
 * @returns {Array<Object>} Array of form values with subElements
 */
export function mapElementsToFormValues(elements, fieldType, slsTypes) {
  const displayLabel = getDisplayLabelForFieldType(fieldType, slsTypes) || fieldType

  if (!elements || elements.length === 0) {
    // Return default empty structure
    return [
      {
        id: null,
        subElements: [
          {
            id: getNextUuid(),
            fieldType,
            displayLabel,
            formValue:
              fieldType === 'DATE' || fieldType === 'PLACE'
                ? { originalText: '', standard: null }
                : { primaryText: '' },
            typeData: { editable: true },
          },
        ],
      },
    ]
  }

  return ensureDefaultStandardField(
    elements.map((element) => ({
      id: element.id,
      // Preserve ALL nested field subElements within the element
      subElements:
        element.subElements?.length > 0
          ? element.subElements.map((subEl) => ({
              ...subEl, // Add all field properties, especially personId, since that is how the save code identifies an existing field.
              fieldType: subEl.fieldType || fieldType,
              formValue: getFormValueFromField(subEl),
              typeData: subEl.typeData || { editable: true },
            }))
          : [
              {
                id: getNextUuid(),
                fieldType,
                displayLabel,
                formValue: getFormValueFromField(element),
                typeData: element.typeData || { editable: true },
              },
            ],
    })),
    fieldType,
    slsTypes
  )
}

/**
 * Populates form values from an existing event.
 * Extracts dates, places, and other fields from the event and transforms them into form-ready values.
 *
 * @param {Object} event - The existing event object from SLS
 * @param {Object} [slsTypes] - Optional SLS types object for getting displayLabels
 * @returns {{dates: Array, places: Array, other: Array}} Object containing form values for dates, places, and other fields
 */
export function populateExistingEventFormValues(event, slsTypes) {
  // Extract dates from the existing event - extract DATE elements from direct children
  const dates = event.subElements?.filter((el) => el.elementType === 'DATE') || []
  const dateFormValues = mapElementsToFormValues(dates, 'DATE', slsTypes)

  // Extract places from the existing event - extract PLACE elements from direct children
  const places = event.subElements?.filter((el) => el.elementType === 'PLACE') || []
  const placeFormValues = mapElementsToFormValues(places, 'PLACE', slsTypes)

  // Extract other fields - extract only FIELD elements that are DIRECT children of the event
  // Do NOT extract fields nested inside PLACE or DATE elements
  // Those nested fields are preserved in the subElements arrays above
  const allFields = event.subElements?.filter((el) => el.elementType === 'FIELD') || []
  const otherFields = allFields.filter((field) => field.fieldType !== 'EVENT_TYPE')
  const otherFormValues =
    otherFields.length > 0
      ? otherFields.map((otherField) => ({
          ...otherField, // Include all field properties
          displayLabel: otherField.displayLabel || otherField.fieldType, // Ensure displayLabel exists
          formValue: getFormValueFromField(otherField),
          typeData: otherField.typeData || { editable: true },
        }))
      : []

  return {
    dates: dateFormValues,
    places: placeFormValues,
    other: otherFormValues,
  }
}
